import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Mic, MicOff, X, Copy, Save, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Global floating dictation button — always visible at bottom-left.
 * Press once = start listening. Transcribed text appears in a panel.
 * User can then copy or save-as-note.
 */
export default function GlobalDictatePanel() {
  const [open, setOpen] = useState(false);
  const [listening, setListening] = useState(false);
  const [text, setText] = useState('');
  const [supported, setSupported] = useState(true);
  const recognitionRef = useRef(null);

  useEffect(() => {
    const SR = typeof window !== 'undefined' && (window.SpeechRecognition || window.webkitSpeechRecognition);
    if (!SR) {
      setSupported(false);
      return;
    }
    const r = new SR();
    r.continuous = true;
    r.interimResults = false;
    r.lang = 'sv-SE';
    r.onresult = (e) => {
      let final = '';
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const res = e.results[i];
        if (res.isFinal) final += res[0].transcript;
      }
      if (final.trim()) {
        setText((prev) => (prev ? prev + ' ' : '') + final.trim());
      }
    };
    r.onerror = (e) => {
      if (e.error === 'not-allowed' || e.error === 'service-not-allowed') {
        toast.error('Mikrofon-åtkomst nekad. Aktivera i webbläsarinställningarna.');
      } else if (e.error !== 'no-speech') {
        toast.error(`Diktering: ${e.error}`);
      }
      setListening(false);
    };
    r.onend = () => setListening(false);
    recognitionRef.current = r;
    return () => { try { r.stop(); } catch { /* ignore */ } };
  }, []);

  const toggle = () => {
    if (!supported) { toast.error('Diktering stöds inte i denna webbläsare'); return; }
    const r = recognitionRef.current;
    if (!r) return;
    if (listening) {
      r.stop();
      setListening(false);
    } else {
      try {
        r.start();
        setListening(true);
        toast.success('🎤 Lyssnar… prata fritt', { duration: 2000 });
      } catch { /* already running */ }
    }
  };

  const copyText = async () => {
    if (!text.trim()) return;
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Kopierat till urklipp 📋');
    } catch { toast.error('Kunde inte kopiera'); }
  };

  const saveAsNote = async () => {
    if (!text.trim()) return;
    try {
      await axios.post(`${API}/notes`, {
        title: `Diktat ${new Date().toLocaleDateString('sv-SE')}`,
        content: text,
        color: '#FFD6E8',
        labels: ['diktat'],
        checklist: [],
      }, { withCredentials: true });
      toast.success('Sparat som anteckning 📝');
      setText('');
      setOpen(false);
    } catch { toast.error('Kunde inte spara'); }
  };

  if (!supported) return null;

  return (
    <>
      {/* Floating button — bottom-left (Pet Coach has bottom-right) */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-6 left-6 z-40 w-14 h-14 rounded-full border-3 border-black shadow-[4px_4px_0_#000] bg-[#FFD6E8] flex items-center justify-center hover:scale-110 transition-transform"
          title="Diktera / Quick Note"
          data-testid="global-dictate-fab"
        >
          <Mic className="w-6 h-6" />
        </button>
      )}

      {open && (
        <div className="fixed bottom-6 left-6 z-50 w-[min(360px,calc(100vw-2rem))] neo-card bg-white flex flex-col" data-testid="global-dictate-panel">
          <div className="p-3 border-b-2 border-black bg-[#FFD6E8] flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Mic className="w-5 h-5" />
              <h3 className="font-['Fredoka'] font-bold text-base">Diktera</h3>
              {listening && <span className="text-[10px] font-bold bg-white border-2 border-black rounded-full px-2 animate-pulse-soft">🔴 Lyssnar…</span>}
            </div>
            <button onClick={() => { if (listening) { recognitionRef.current?.stop(); setListening(false); } setOpen(false); }} className="neo-btn p-1.5 bg-white" data-testid="global-dictate-close">
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="p-3 space-y-2">
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Tryck på mic-knappen och prata — texten dyker upp här…"
              rows={5}
              className="w-full px-3 py-2 rounded-xl border-2 border-black text-sm resize-none"
              data-testid="global-dictate-text"
            />

            <button
              onClick={toggle}
              className={`neo-btn w-full py-3 text-sm font-bold flex items-center justify-center gap-2 ${listening ? 'bg-[#FFC8DD] animate-pulse-soft' : 'bg-[#C8B6FF]'}`}
              data-testid="global-dictate-toggle"
            >
              {listening ? <><MicOff className="w-5 h-5" /> Stoppa</> : <><Mic className="w-5 h-5" /> Starta diktering</>}
            </button>

            <div className="flex gap-2">
              <button
                onClick={copyText}
                disabled={!text.trim()}
                className="neo-btn flex-1 py-2 bg-white text-xs font-bold flex items-center justify-center gap-1 disabled:opacity-50"
                data-testid="global-dictate-copy"
              >
                <Copy className="w-3.5 h-3.5" /> Kopiera
              </button>
              <button
                onClick={saveAsNote}
                disabled={!text.trim()}
                className="neo-btn flex-1 py-2 bg-[#B9FBC0] text-xs font-bold flex items-center justify-center gap-1 disabled:opacity-50"
                data-testid="global-dictate-save"
              >
                <Save className="w-3.5 h-3.5" /> Spara som note
              </button>
            </div>

            <p className="text-[10px] opacity-60 italic text-center">
              <Sparkles className="w-3 h-3 inline" /> Tips: Kan användas överallt i appen — dra knappen till ett hörn om den är i vägen
            </p>
          </div>
        </div>
      )}
    </>
  );
}
